/**
 * Hero dock login ↔ register crossfade shell (`main_screen_variant: hero_dock`).
 * Uses hero_dock-local `LoginSheetContent` / `RegisterSheetContent` only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import Animated, { Extrapolation, interpolate, runOnJS, useAnimatedReaction, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { LoginSheetContent } from './LoginSheetContent';
import { RegisterSheetContent } from './RegisterSheetContent';
import type { MainScreenAuthFormsBaseProps } from './mainScreenAuthFormsTypes';
import {
  MAIN_SCREEN_V3_FORM_TRANSFORM_ORIGIN,
  mainScreenV3AuthFormShell,
  mainScreenV3LoginLayerAt,
  mainScreenV3RegisterLayerAt,
} from './mainScreenAuthFormsMotion';
import {
  androidMainScreenAuthFormLayerSurface,
  androidMainScreenAuthFormShellSurface,
} from '../../../shared/auth/mainScreenAndroidFormSurface';
import { appTheme, useThemeColors } from '../../../shared/theme';

const ANDROID_MAIN_FORM_SHELL_CAP = Platform.OS === 'android';
/** PWA reanimated stub does not re-render on shared-value writes — use React state for shell height. */
const WEB_FORM_SHELL_HEIGHT = Platform.OS === 'web';

/** `default` / `mainScreenV2` — flat shell (no extra inset). */
const main_screen_auth_shell_static = StyleSheet.create({
  shell: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
});

/**
 * v3 in-main login/register (`MainScreen` + `AuthSheetHost` main-screen presentation).
 * Variants: **`mainScreenV3`** (opacity/translate/scale crossfade + padded shell), **`mainScreenV2`**, **`default`** (`scaleY`).
 */
export function MainScreenAuthForms({
  swap,
  onDismiss,
  registerSubData,
  mainScreenScrollMaxHeight,
  onSwitchToRegister,
  onSwitchToLogin,
  formMotionVariant = 'default',
  onKeyboardPanelNudge,
}: MainScreenAuthFormsBaseProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const is_main_screen_v2_forms = formMotionVariant === 'mainScreenV2';
  const is_main_screen_v3_forms = formMotionVariant === 'mainScreenV3';
  const login_h = useSharedValue(0);
  const register_h = useSharedValue(0);
  const scroll_max_sv = useSharedValue(mainScreenScrollMaxHeight);
  const [web_login_h, set_web_login_h] = useState(0);
  const [web_register_h, set_web_register_h] = useState(0);
  const [activeLayer, setActiveLayer] = useState<'login' | 'register'>('login');
  const mounted_ref = useRef(true);

  useEffect(() => {
    mounted_ref.current = true;
    return () => {
      mounted_ref.current = false;
    };
  }, []);

  useEffect(() => {
    scroll_max_sv.value = mainScreenScrollMaxHeight;
  }, [mainScreenScrollMaxHeight, scroll_max_sv]);

  const applyActiveLayer = useCallback((layer: 'login' | 'register') => {
    if (mounted_ref.current) {
      setActiveLayer(layer);
    }
  }, []);

  useAnimatedReaction(
    () => (swap.value < 0.5 ? 0 : 1),
    (curr, prev) => {
      if (prev != null && curr === prev) {
        return;
      }
      runOnJS(applyActiveLayer)(curr === 0 ? 'login' : 'register');
    },
  );

  const set_login_layout_h = useCallback(
    (h: number) => {
      if (WEB_FORM_SHELL_HEIGHT) {
        set_web_login_h(prev => (Math.abs(prev - h) < 1 ? prev : h));
        return;
      }
      login_h.value = h;
    },
    [login_h],
  );
  const set_register_layout_h = useCallback(
    (h: number) => {
      if (WEB_FORM_SHELL_HEIGHT) {
        set_web_register_h(prev => (Math.abs(prev - h) < 1 ? prev : h));
        return;
      }
      register_h.value = h;
    },
    [register_h],
  );

  const shell_animated_style = useAnimatedStyle(() => {
    'worklet';
    if (WEB_FORM_SHELL_HEIGHT) {
      return {};
    }
    const lh = login_h.value;
    const rh = register_h.value;
    const t = swap.value;
    const activeH = t < 0.5 ? lh : rh;
    const taller = lh > rh ? lh : rh;
    const sm = scroll_max_sv.value;
    const fallback = Math.min(320, Math.max(200, Math.round(sm * 0.45)));
    const raw = activeH > 8 ? activeH : Math.max(taller, fallback);
    const h = sm > 8 ? Math.min(raw, sm) : raw;
    if (ANDROID_MAIN_FORM_SHELL_CAP) {
      // Match iOS intrinsic height — a large minHeight floor extends the white card
      // into the Android auth chip row (chips paint above the panel).
      return { height: Math.max(h, 1), minHeight: 0 };
    }
    return { height: h };
  });

  const web_shell_height_style = (() => {
    if (!WEB_FORM_SHELL_HEIGHT) {
      return null;
    }
    const lh = web_login_h;
    const rh = web_register_h;
    const activeH = activeLayer === 'login' ? lh : rh;
    const taller = lh > rh ? lh : rh;
    const sm = mainScreenScrollMaxHeight ?? 0;
    const fallback = Math.min(320, Math.max(200, Math.round(sm * 0.45)));
    const raw = activeH > 8 ? activeH : Math.max(taller, fallback);
    const h = sm > 8 ? Math.min(raw, sm) : raw;
    return { height: Math.max(h, 1), minHeight: 0 };
  })();

  const login_style = useAnimatedStyle(() => {
    'worklet';
    const t = swap.value;
    if (is_main_screen_v3_forms) {
      return mainScreenV3LoginLayerAt(t);
    }
    const scale_y_fully_out = is_main_screen_v2_forms ? 0.85 : 0.85;
    return {
      opacity: interpolate(t, [0, 1], [1, 0], Extrapolation.CLAMP),
      transform: [
        { translateY: interpolate(t, [0, 1], [0, 0], Extrapolation.CLAMP) },
        { scaleY: interpolate(t, [0, 1], [1, scale_y_fully_out], Extrapolation.CLAMP) },
      ],
    };
  }, [is_main_screen_v2_forms, is_main_screen_v3_forms]);

  const register_style = useAnimatedStyle(() => {
    'worklet';
    const t = swap.value;
    if (is_main_screen_v3_forms) {
      return mainScreenV3RegisterLayerAt(t);
    }
    const scale_y_fully_in = is_main_screen_v2_forms ? 1 : 1;
    return {
      opacity: interpolate(t, [0, 1], [0, 1], Extrapolation.CLAMP),
      transform: [
        { translateY: interpolate(t, [0, 1], [0, 0], Extrapolation.CLAMP) },
        { scaleY: interpolate(t, [0, 1], [0, scale_y_fully_in], Extrapolation.CLAMP) },
      ],
    };
  }, [is_main_screen_v2_forms, is_main_screen_v3_forms]);

  const shell_style = is_main_screen_v3_forms ? mainScreenV3AuthFormShell.outer : main_screen_auth_shell_static.shell;
  const android_main_shell_surface = androidMainScreenAuthFormShellSurface(colors);
  const android_form_layer_surface = androidMainScreenAuthFormLayerSurface(colors);
  const form_layer_style = [
    StyleSheet.absoluteFillObject,
    ANDROID_MAIN_FORM_SHELL_CAP ? { minHeight: 0 } : null,
    {
      justifyContent: 'flex-start' as const,
      transformOrigin: is_main_screen_v3_forms ? MAIN_SCREEN_V3_FORM_TRANSFORM_ORIGIN : ('50% 15% 0px' as const),
    },
  ];

  return (
    <Animated.View
      style={[
        shell_style,
        android_main_shell_surface,
        shell_animated_style,
        web_shell_height_style,
        mainScreenScrollMaxHeight > 0 ? { maxHeight: mainScreenScrollMaxHeight, width: '100%', minHeight: 0, overflow: 'hidden' as const } : null,
      ]}>
      <Animated.View style={[...form_layer_style, android_form_layer_surface, login_style]} pointerEvents={activeLayer === 'login' ? 'auto' : 'none'} collapsable={false}>
        <LoginSheetContent
          onDismiss={onDismiss}
          onInlineSwitchToRegister={onSwitchToRegister}
          mainScreenIntrinsic
          mainScreenScrollMaxHeight={mainScreenScrollMaxHeight}
          onFormLayoutHeight={set_login_layout_h}
          onMainScreenKeyboardNudge={onKeyboardPanelNudge}
        />
      </Animated.View>
      <Animated.View style={[...form_layer_style, android_form_layer_surface, register_style]} pointerEvents={activeLayer === 'register' ? 'auto' : 'none'} collapsable={false}>
        <RegisterSheetContent
          onDismiss={onDismiss}
          onInlineSwitchToLogin={onSwitchToLogin}
          subData={registerSubData}
          mainScreenIntrinsic
          mainScreenScrollMaxHeight={mainScreenScrollMaxHeight}
          onFormLayoutHeight={set_register_layout_h}
          onMainScreenKeyboardNudge={onKeyboardPanelNudge}
        />
      </Animated.View>
    </Animated.View>
  );
}
